import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useErrorHandler } from './useErrorHandler';
import { validateUserSession } from '@/lib/dataValidation';

interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login?: string;
}

interface SecureAdminAuthContextType {
  isAdminAuthenticated: boolean;
  adminUser: AdminUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  createAuditLog: (action: string, details?: any) => Promise<void>;
}

const SecureAdminAuthContext = createContext<SecureAdminAuthContextType | undefined>(undefined);

export const useSecureAdminAuth = () => {
  const context = useContext(SecureAdminAuthContext);
  const { handleError } = useErrorHandler('useSecureAdminAuth');
  
  if (context === undefined) {
    throw handleError(new Error('useSecureAdminAuth must be used within a SecureAdminAuthProvider'));
  }
  return context;
};

interface SecureAdminAuthProviderProps {
  children: ReactNode;
}

export const SecureAdminAuthProvider: React.FC<SecureAdminAuthProviderProps> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { handleError, handleAsyncError } = useErrorHandler('SecureAdminAuthProvider');

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      if (!validateUserSession(user)) {
        setIsAdminAuthenticated(false);
        setAdminUser(null);
        setIsLoading(false);
        return;
      }

      // Vérifier si l'utilisateur est admin
      const { data: adminData, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        handleError(error, 'checkAdminStatus-query');
        setIsAdminAuthenticated(false);
        setAdminUser(null);
      } else if (adminData) {
        setIsAdminAuthenticated(true);
        setAdminUser(adminData);
        
        // Mettre à jour la dernière connexion
        await supabase
          .from('admin_users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', adminData.id);
      } else {
        setIsAdminAuthenticated(false);
        setAdminUser(null);
      }
    } catch (error) {
      handleError(error, 'checkAdminStatus-outer');
      setIsAdminAuthenticated(false);
      setAdminUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('🔐 Starting admin login for:', email);
      setIsLoading(true);
      
      console.log('📋 Checking email whitelist...');
      // Vérifier si l'email est dans la whitelist
      const { data: whitelistCheck, error: whitelistError } = await supabase
        .rpc('is_email_whitelisted', { email_to_check: email });

      console.log('📋 Whitelist result:', { whitelistCheck, whitelistError });

      if (whitelistError) {
        console.error('❌ Whitelist check error:', whitelistError);
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'Email not whitelisted',
          p_user_agent: navigator.userAgent
        });
        return false;
      }

      if (!whitelistCheck) {
        console.error('❌ Email not whitelisted:', email);
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'Email not in whitelist',
          p_user_agent: navigator.userAgent
        });
        return false;
      }

      console.log('🚫 Checking failed login attempts...');
      // Vérifier les tentatives d'échec répétées
      const { data: isBlocked, error: blockError } = await supabase
        .rpc('check_failed_login_attempts', { p_email: email });

      console.log('🚫 Block check result:', { isBlocked, blockError });

      if (blockError) {
        console.error('Block check error:', blockError);
        return false;
      }

      if (isBlocked) {
        console.error('Too many failed attempts for:', email);
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'Account temporarily blocked - too many failed attempts',
          p_user_agent: navigator.userAgent
        });
        return false;
      }
      
      console.log('🔑 Attempting Supabase authentication...');
      // Authentification Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      console.log('🔑 Auth result:', { 
        user: authData?.user?.id, 
        session: !!authData?.session,
        error: authError 
      });

      if (authError || !authData.user) {
        console.error('❌ Auth error:', authError);
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'Invalid credentials',
          p_user_agent: navigator.userAgent
        });
        return false;
      }

      console.log('👑 Checking admin status for user:', authData.user.id);
      // Vérifier si l'utilisateur est admin
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', authData.user.id)
        .eq('is_active', true)
        .maybeSingle();

      console.log('👑 Admin check result:', { adminData, adminError });

      if (adminError) {
        console.error('❌ Admin check error:', adminError);
        await supabase.auth.signOut();
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'Admin verification failed',
          p_user_agent: navigator.userAgent
        });
        return false;
      }

      if (!adminData) {
        console.error('❌ User is not an admin');
        await supabase.auth.signOut();
        await supabase.rpc('log_admin_login_attempt', {
          p_email: email,
          p_success: false,
          p_failure_reason: 'User not an admin',
          p_user_agent: navigator.userAgent
        });
        return false;
      }

      console.log('✅ Admin authentication successful!');
      setIsAdminAuthenticated(true);
      setAdminUser(adminData);

      // Créer une session admin
      const sessionToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 30); // 30 minutes seulement

      await supabase.from('admin_sessions').insert({
        admin_user_id: adminData.id,
        session_token: sessionToken,
        expires_at: expiresAt.toISOString(),
        ip_address: await getClientIP(),
        user_agent: navigator.userAgent
      });

      // Mettre à jour la dernière connexion
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', adminData.id);

      // Log de connexion réussie
      await supabase.rpc('log_admin_login_attempt', {
        p_email: email,
        p_success: true,
        p_user_agent: navigator.userAgent
      });

      // Log de connexion dans l'audit
      await createAuditLog('admin_login', { email });

      return true;
    } catch (error) {
      console.error('Login error:', error);
      await supabase.rpc('log_admin_login_attempt', {
        p_email: email,
        p_success: false,
        p_failure_reason: 'System error',
        p_user_agent: navigator.userAgent
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (adminUser) {
        // Log de déconnexion
        await createAuditLog('admin_logout');
        
        // Désactiver les sessions actives
        await supabase
          .from('admin_sessions')
          .update({ is_active: false })
          .eq('admin_user_id', adminUser.id)
          .eq('is_active', true);
      }

      await supabase.auth.signOut();
      setIsAdminAuthenticated(false);
      setAdminUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const createAuditLog = async (action: string, details?: any): Promise<void> => {
    if (!adminUser) return;

    try {
      await supabase.rpc('create_audit_log', {
        p_admin_user_id: adminUser.id,
        p_action: action,
        p_table_name: details?.table_name || null,
        p_record_id: details?.record_id || null,
        p_old_values: details?.old_values || null,
        p_new_values: details?.new_values || null,
        p_ip_address: await getClientIP(),
        p_user_agent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error creating audit log:', error);
    }
  };

  // Fonction helper pour obtenir l'IP client
  const getClientIP = async (): Promise<string | null> => {
    try {
      // En production, vous devriez utiliser un service pour obtenir la vraie IP
      return null; // Placeholder
    } catch {
      return null;
    }
  };

  return (
    <SecureAdminAuthContext.Provider value={{
      isAdminAuthenticated,
      adminUser,
      isLoading,
      login,
      logout,
      createAuditLog
    }}>
      {children}
    </SecureAdminAuthContext.Provider>
  );
};